// FinKloz — Transaction Matching Engine
// Multi-pass matching of bank statement transactions to GL entries.
// Handles batch payments (one bank txn = sum of multiple GL entries).

// ─── Description similarity (token-based Jaccard) ───
function descriptionSimilarity(a, b) {
  if (!a || !b) return 0;
  const normalize = s => s.toLowerCase().replace(/[^a-z0-9\s]/g, '');
  const tokensA = new Set(normalize(a).split(/\s+/).filter(Boolean));
  const tokensB = new Set(normalize(b).split(/\s+/).filter(Boolean));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  const intersection = [...tokensA].filter(t => tokensB.has(t));
  const union = new Set([...tokensA, ...tokensB]);
  return intersection.length / union.size;
}

function toDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function dayDiff(d1, d2) {
  if (!d1 || !d2) return Infinity;
  return Math.abs(Math.round((d1 - d2) / 86400000));
}

function bankNet(txn) { return (txn.credit || 0) - (txn.debit || 0); }
// For GL entries: use txnDebit/txnCredit (original currency) if available, otherwise CAD amounts
function glNet(txn) {
  if (txn.txnDebit !== undefined || txn.txnCredit !== undefined) {
    return (txn.txnDebit || 0) - (txn.txnCredit || 0);
  }
  return (txn.debit || 0) - (txn.credit || 0);
}

// ─── Detect if a bank transaction looks like a batch payment ───
function isBatchPayment(txn) {
  const desc = (txn.description || '').toLowerCase();
  return /d[eé]p[oô]t\s*direct|batch|total|sdp|grads|virement\s*global|paiement\s*group/i.test(desc);
}

// ─── Find subset of GL entries whose absolute sum matches a target amount ───
// Uses a greedy approach sorting by date proximity, then tries combinations
function findMatchingSubset(candidates, targetAbs, tolerance = 0.10) {
  if (candidates.length === 0) return null;

  // Sort by absolute amount descending for greedy approach
  const sorted = [...candidates].sort((a, b) => Math.abs(glNet(b)) - Math.abs(glNet(a)));

  // Greedy: accumulate until we match
  let runningSum = 0;
  const batch = [];
  for (const gl of sorted) {
    runningSum += Math.abs(glNet(gl));
    batch.push(gl);
    if (Math.abs(runningSum - targetAbs) <= tolerance) {
      return batch;
    }
    if (runningSum > targetAbs + tolerance) {
      // Overshot — try without this item
      runningSum -= Math.abs(glNet(gl));
      batch.pop();
    }
  }

  // If greedy didn't work, try all items (maybe they all sum to the target)
  const allSum = candidates.reduce((s, g) => s + Math.abs(glNet(g)), 0);
  if (Math.abs(allSum - targetAbs) <= tolerance) {
    return candidates;
  }

  return null;
}

// ─── Main matching algorithm ───
export function matchTransactions(bankTxns, glEntries, options = {}) {
  const {
    dateTolerance = 7,
    amountTolerance = 0.05,
    descThreshold = 0.4,
  } = options;

  const matched = [];
  const usedBankIds = new Set();
  const usedGLIds = new Set();

  // ═══ Pass 0: Batch matching — FIRST, before 1:1 ═══
  // Bank transactions that look like batch payments get matched to groups of GL entries
  for (const btxn of bankTxns) {
    if (usedBankIds.has(btxn.id)) continue;
    if (!isBatchPayment(btxn)) continue;

    const bAbs = Math.abs(bankNet(btxn));
    const bDate = toDate(btxn.date);
    if (!bDate || bAbs < 1) continue;

    // Find GL entries on the same date or within 2 days
    const candidates = glEntries.filter(gl => {
      if (usedGLIds.has(gl.id)) return false;
      const gDate = toDate(gl.date);
      return gDate && dayDiff(bDate, gDate) <= 2;
    });

    if (candidates.length < 2) continue;

    const batch = findMatchingSubset(candidates, bAbs);
    if (batch && batch.length >= 2) {
      matched.push({
        bankTxn: btxn,
        glEntries: batch,
        confidence: 85,
        matchType: 'batch',
      });
      usedBankIds.add(btxn.id);
      for (const gl of batch) usedGLIds.add(gl.id);
    }
  }

  // ═══ Pass 1: Exact amount + exact date (confidence 100) ═══
  for (const btxn of bankTxns) {
    if (usedBankIds.has(btxn.id)) continue;
    const bAbs = Math.abs(bankNet(btxn));
    const bDate = toDate(btxn.date);
    if (!bDate) continue;

    for (const gl of glEntries) {
      if (usedGLIds.has(gl.id)) continue;
      const gAbs = Math.abs(glNet(gl));
      const gDate = toDate(gl.date);
      if (!gDate) continue;

      if (Math.abs(bAbs - gAbs) < 0.01 && dayDiff(bDate, gDate) === 0) {
        matched.push({ bankTxn: btxn, glEntry: gl, confidence: 100, matchType: 'exact' });
        usedBankIds.add(btxn.id);
        usedGLIds.add(gl.id);
        break;
      }
    }
  }

  // ═══ Pass 2: Exact amount + date within 3 days (confidence 90) ═══
  for (const btxn of bankTxns) {
    if (usedBankIds.has(btxn.id)) continue;
    const bAbs = Math.abs(bankNet(btxn));
    const bDate = toDate(btxn.date);
    if (!bDate) continue;

    let bestMatch = null;
    let bestDays = Infinity;

    for (const gl of glEntries) {
      if (usedGLIds.has(gl.id)) continue;
      const gAbs = Math.abs(glNet(gl));
      const gDate = toDate(gl.date);
      if (!gDate) continue;

      const days = dayDiff(bDate, gDate);
      if (Math.abs(bAbs - gAbs) < 0.01 && days <= 3 && days < bestDays) {
        bestDays = days;
        bestMatch = gl;
      }
    }

    if (bestMatch) {
      matched.push({ bankTxn: btxn, glEntry: bestMatch, confidence: 90, matchType: 'date_close' });
      usedBankIds.add(btxn.id);
      usedGLIds.add(bestMatch.id);
    }
  }

  // ═══ Pass 3: Exact amount + date within tolerance + desc similarity (confidence 75) ═══
  for (const btxn of bankTxns) {
    if (usedBankIds.has(btxn.id)) continue;
    const bAbs = Math.abs(bankNet(btxn));
    const bDate = toDate(btxn.date);
    if (!bDate) continue;

    let bestMatch = null;
    let bestScore = 0;

    for (const gl of glEntries) {
      if (usedGLIds.has(gl.id)) continue;
      const gAbs = Math.abs(glNet(gl));
      const gDate = toDate(gl.date);
      if (!gDate) continue;

      const days = dayDiff(bDate, gDate);
      if (Math.abs(bAbs - gAbs) < 0.01 && days <= dateTolerance) {
        const sim = descriptionSimilarity(btxn.description, gl.description);
        if (sim >= descThreshold && sim > bestScore) {
          bestScore = sim;
          bestMatch = gl;
        }
      }
    }

    if (bestMatch) {
      matched.push({ bankTxn: btxn, glEntry: bestMatch, confidence: 75, matchType: 'desc_similar' });
      usedBankIds.add(btxn.id);
      usedGLIds.add(bestMatch.id);
    }
  }

  // ═══ Pass 4: Remaining batch matching (non-tagged batch payments) ═══
  // For any remaining unmatched bank txns, try to find GL subsets that sum to the amount
  for (const btxn of bankTxns) {
    if (usedBankIds.has(btxn.id)) continue;
    const bAbs = Math.abs(bankNet(btxn));
    const bDate = toDate(btxn.date);
    if (!bDate || bAbs < 1) continue;

    const candidates = glEntries.filter(gl => {
      if (usedGLIds.has(gl.id)) return false;
      const gDate = toDate(gl.date);
      return gDate && dayDiff(bDate, gDate) <= 3;
    });

    if (candidates.length < 2) continue;

    const batch = findMatchingSubset(candidates, bAbs);
    if (batch && batch.length >= 2) {
      matched.push({
        bankTxn: btxn,
        glEntries: batch,
        confidence: 70,
        matchType: 'batch',
      });
      usedBankIds.add(btxn.id);
      for (const gl of batch) usedGLIds.add(gl.id);
    }
  }

  // Collect unmatched
  const unmatchedBank = bankTxns.filter(b => !usedBankIds.has(b.id));
  const unmatchedGL = glEntries.filter(g => !usedGLIds.has(g.id));

  const bankCount = bankTxns.length;
  const glCount = glEntries.length;
  const matchedBankCount = bankCount - unmatchedBank.length;
  const matchedGLCount = glCount - unmatchedGL.length;

  return {
    matched,
    unmatchedBank,
    unmatchedGL,
    stats: {
      bankCount,
      glCount,
      matchedCount: matchedBankCount,
      matchRate: bankCount > 0 ? Math.round(matchedBankCount / bankCount * 100) : 0,
      glMatchRate: glCount > 0 ? Math.round(matchedGLCount / glCount * 100) : 0,
    },
  };
}
